import { Component, effect, HostListener, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, Params, RouterLink } from "@angular/router";
import { SharedLoginRepository, LoginResponse, LoginRequest,
	PasswordResetRequest, UpdatePasswordRequest} from "@shared/src/lib/repositories/shared-login-repository";
import { CivilianRegistrationRepository } from "src/app/repositories/registration/civilian-registration-repository";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { Subject } from "rxjs";
import { ScreenStore, UserStore } from "@shared/src/public-api";
import { ToastService } from "@shared/src/lib/services/toast.service";

@Component({
	selector: "jhmc-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"],
	imports: [RouterLink, MatFormFieldModule, MatInputModule, FormsModule]
})
export class LoginComponent implements OnDestroy, OnInit
{
	private loginRepository: SharedLoginRepository;
	private router: Router;
	private route: ActivatedRoute;

	public username: string = "";
	public password: string = "";
	public accessToken: string = "access_token";
	public forgotPassword: boolean = false;
	public warningMessage: string;
	public passwordReset: boolean = false;
	public changePassword: boolean = false;
	public newPassword: string;
	public newConfirmedPassword: string;
	public passwordsDontMatch: boolean = false;
	public passwordResetId: string;
	public confirmationId: string;
	public confirmationSuccessful: boolean = false;
	public initialState: boolean = true;
	public showLogin: boolean = false;
	public loginMessage: string;
	public screenSize: string;
	public ngDestroyed$: Subject<any> = new Subject();
	public isMobile: boolean = false;
	public toastService: ToastService;

	readonly screenStore = inject(ScreenStore);
	readonly userStore = inject(UserStore);

	private emailRegex: RegExp = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;

	public constructor(loginRepository: SharedLoginRepository, router: Router,
		route: ActivatedRoute, private civilianRegistrationRepository: CivilianRegistrationRepository,
		toastService: ToastService)
	{
		this.loginRepository = loginRepository;
		this.router = router;
		this.route = route;
		this.toastService = toastService;

		effect(() => {
			this.screenSize = this.screenStore.screenSize();
		});
	}

	ngOnInit(): void
	{
		this.route.queryParams.subscribe((params: Params) =>
		{
			if (params.resetId)
			{
				this.passwordResetId = params.resetId;
				this.changePassword = true;
				this.initialState = false;
			}

			this.confirmationId = params.confirmationId;
			if (this.confirmationId)
			{
				this.civilianRegistrationRepository.confirmNewCivilianRegistration(this.confirmationId).subscribe({
					next: () =>
					{
						this.confirmationSuccessful = true;
						this.initialState = false;
					},
					error: (error: Error) => {
						alert("Unable to confirm civilian=" + error.message);
					}
				});
			}

			this.loginMessage = params.loginMessage;
		});
	}

	ngOnDestroy(): void
	{
		this.ngDestroyed$.next(true);
	}

	@HostListener("document:keydown.enter")
	public onDocumentKeydownEnter(): void
	{
		if (!this.forgotPassword && !this.passwordReset && !this.changePassword)
		{
			this.login();
		}
		else if (this.forgotPassword && !this.passwordReset && !this.changePassword)
		{
			this.sendResetEmail();
		}
		else if (!this.forgotPassword && this.passwordReset && !this.changePassword)
		{
			this.passwordReset = false;
		}
		else if (!this.forgotPassword && !this.passwordReset && this.changePassword)
		{
			this.updatePassword();
		}
	}

	public login(): void
	{
		if (this.username, this.password)
		{
			const loginRequest: LoginRequest = new LoginRequest(this.username, this.password);
			this.loginRepository.login(loginRequest).subscribe((loginResponse: LoginResponse) =>
			{
				if (loginResponse.jwt)
				{
					this.userStore.login(loginResponse);
					this.router.navigate(["/inbox"]);
				}
				else if (loginResponse.pendingUserCivilianId)
				{
					this.router.navigate(["/sign-up"], {
						queryParams: {
							pendingUserCivilianId: loginResponse.pendingUserCivilianId
						}
					});
				}
				else
				{
					localStorage.removeItem("access_token");
				}

				if (loginResponse.isAttorney && loginResponse.isInactiveAttorney) {
					this.warningMessage = "Your active attorney status has not been confirmed yet, "
					+ "please reach out to customer support if problem persists longer than 24 hours.";
				}
				else
				{
					this.warningMessage = "Invalid login, please try again.";
				}
			});
		}
		else
		{
			this.warningMessage = "Invalid login, please try again.";
		}
	}

	public sendResetEmail(): void
	{
		if (!this.emailRegex.exec(this.username))
		{
			this.warningMessage = "Please enter a valid email address.";
		}
		else
		{
			this.loginRepository.resetPassword(new PasswordResetRequest(this.username)).subscribe({
				next: () =>
				{
					this.warningMessage = null;
					this.forgotPassword = false;
					this.passwordReset = true;
				},
				error: (error: Error) => {
					this.toastService.show("Unable to reset password due to the following error: " + error.message);
				}
			});
		}
	}

	public checkConfirmedPassword(): void
	{
		setTimeout(() =>
		{
			if (this.newPassword !== this.newConfirmedPassword && (this.newPassword && this.newConfirmedPassword))
			{
				this.passwordsDontMatch = true;
			}
			else
			{
				this.passwordsDontMatch = false;
			}
		});
	}

	public updatePassword(): void
	{
		if (!this.newPassword)
		{
			this.toastService.show("You must set a password in the 'Password' field.", "error");
		}
		if (!this.newConfirmedPassword)
		{
			this.toastService.show("You must set a passowrd in the 'Confirm Password' field.", "error");
		}
		else if (this.passwordsDontMatch)
		{
			this.toastService.show("The 'Password' and 'Confirm Password' fields don't match, "
				+ "and the must match in order to reset your password.", "error");
		}
		else
		{
			this.loginRepository.updatePasswordWithPin(this.passwordResetId, new UpdatePasswordRequest(this.newConfirmedPassword)).subscribe({
				next: () =>
				{
					this.showLogin = true;
					this.passwordResetId = null;
					this.changePassword = false;
					this.newPassword = null;
					this.newConfirmedPassword = null;
				},
				error: (error: Error) => {
					this.toastService.show("Unable to update password due to the following error: " + error.message, "error");
				}
			});
		}
	}
}
